import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CheckIcon, ChevronDownIcon, Search } from 'lucide-react';
import {
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/design-system/lib/cn';

/** Ícone de uma opção: `iconClassName` dá a cor por token e `iconStyle`, a que é dado (`toneStyle`). */
export type SelectOptionIcon = ComponentType<{
  className?: string;
  style?: CSSProperties;
  'aria-hidden'?: boolean;
}>;

/** `pin` fixa a opção no topo, fora da ordem alfabética ("Todos os…", "Nenhum"). */
export type SelectOption = {
  value: string;
  label: string;
  pin?: boolean;
  icon?: SelectOptionIcon;
  iconClassName?: string;
  iconStyle?: CSSProperties;
  /**
   * Título do grupo da opção ("Habilitados na função"). Os grupos saem na ordem em que
   * aparecem em `options`, cada um com o título e em ordem alfabética, e a busca atravessa todos.
   */
  group?: string;
  /** Elemento antes do nome (o avatar de um membro), no botão e na lista. */
  leading?: ReactNode;
  /**
   * Texto curto embaixo do nome, só na lista (o que a opção quer dizer). É a descrição
   * acessível da opção; o nome continua só o `label`.
   */
  description?: string;
};

/** Acima disto a lista ganha o campo "Pesquisar..." (salvo `searchable` explícito). */
export const SEARCH_THRESHOLD = 8;

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });

/** Sem acento e em minúsculas, para a busca. */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * As fixadas na ordem dada, depois as demais em ordem alfabética (pt-BR, sem acento) — ou,
 * com `ordered`, na ordem dada (a mais provável primeiro), agrupadas do mesmo jeito.
 */
export function sortOptions(
  options: readonly SelectOption[],
  ordered = false
): {
  pinned: SelectOption[];
  rest: SelectOption[];
} {
  const groups = [...new Set(options.filter((o) => !o.pin).map((o) => o.group ?? ''))];
  const rank = (o: SelectOption) => groups.indexOf(o.group ?? '');

  return {
    pinned: options.filter((o) => o.pin),
    rest: options
      .filter((o) => !o.pin)
      .sort((a, b) => rank(a) - rank(b) || (ordered ? 0 : collator.compare(a.label, b.label))),
  };
}

/**
 * Opções visíveis para a busca: sem busca, as fixadas e depois as demais; com busca, só
 * as não fixadas que contêm o texto (sem acento nem maiúscula).
 */
export function filterOptions(
  options: readonly SelectOption[],
  query: string,
  ordered = false
): { visible: SelectOption[]; pinnedCount: number } {
  const { pinned, rest } = sortOptions(options, ordered);
  const q = fold(query.trim());

  if (!q) return { visible: [...pinned, ...rest], pinnedCount: pinned.length };

  return { visible: rest.filter((o) => fold(o.label).includes(q)), pinnedCount: 0 };
}

export type SimpleSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  'aria-label'?: string;
  id?: string;
  size?: 'md' | 'sm';
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Campo de busca na lista. Padrão: só com mais de `SEARCH_THRESHOLD` opções. */
  searchable?: boolean;
  /**
   * Mantém as opções na ordem recebida, em vez da alfabética — quando a ordem diz algo
   * (a mais provável primeiro, como a situação do membro).
   */
  ordered?: boolean;
};

/**
 * Seleção do design (`Select.dc.html`): botão `combobox` que abre uma lista em popover
 * (Radix Popover modal — portal, posição, foco, clique fora e Esc, também dentro de
 * diálogos). As opções saem em ordem alfabética (ou na recebida, com `ordered`), com as fixadas no topo e um
 * separador; com muitas opções aparece "Pesquisar...". Teclado: setas, Home/End,
 * Enter, Esc, Tab e, sem busca, a letra inicial.
 */
export function SimpleSelect({
  value,
  onValueChange,
  options,
  size = 'md',
  placeholder = 'Selecionar',
  className,
  disabled,
  searchable,
  ordered = false,
  id,
  'aria-label': ariaLabel,
}: SimpleSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const uid = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const canSearch = searchable ?? options.length > SEARCH_THRESHOLD;
  // Ordenar as opções custa com listas grandes (membros): só refaz quando elas ou a busca mudam.
  const { visible, pinnedCount } = useMemo(
    () => filterOptions(options, query, ordered),
    [options, query, ordered]
  );
  const current = options.find((o) => o.value === value);
  const described = options.some((o) => o.description);
  const listId = `${uid}-list`;
  const optionId = (i: number) => `${uid}-opt-${i}`;
  const activeId = visible[active] ? optionId(active) : undefined;

  // Rola só a lista (nunca a página ou o diálogo) para manter a opção ativa à vista.
  const scrollToActive = () => {
    const box = listRef.current;
    const item = activeId ? document.getElementById(activeId) : null;

    if (!box || !item) return;
    if (item.offsetTop < box.scrollTop) box.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = item.offsetTop + item.offsetHeight - box.clientHeight;
    }
  };

  const latestScroll = useRef(scrollToActive);

  useEffect(() => {
    latestScroll.current = scrollToActive;
    scrollToActive();
  });

  // Ao abrir, a altura máxima da lista só vale depois que o popover se posiciona; o
  // observador rola de novo quando o tamanho dela muda.
  const attachList = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;

    if (!node || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => latestScroll.current());
    observer.observe(node);

    return () => {
      observer.disconnect();
      listRef.current = null;
    };
  }, []);

  const openChange = (next: boolean) => {
    if (next) {
      setQuery('');
      setActive(
        Math.max(
          0,
          filterOptions(options, '', ordered).visible.findIndex((o) => o.value === value)
        )
      );
    }

    setOpen(next);
  };

  const choose = (option: SelectOption) => {
    onValueChange(option.value);
    setOpen(false);
  };

  const move = (to: number) => {
    if (visible.length) setActive(Math.min(visible.length - 1, Math.max(0, to)));
  };

  const onListKey = (e: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => move(active + 1),
      ArrowUp: () => move(active - 1),
      Home: () => move(0),
      End: () => move(visible.length - 1),
      Enter: () => {
        const option = visible[active];

        if (option) choose(option);
      },
    };
    const action = keys[e.key];

    if (action) {
      e.preventDefault();
      action();

      return;
    }

    if (e.key === 'Tab') {
      setOpen(false);

      return;
    }

    // Sem busca, a letra pula para a próxima opção que começa com ela.
    if (!canSearch && e.key.length === 1) {
      const key = fold(e.key);
      const starts = (o: SelectOption) => fold(o.label).startsWith(key);
      const next = visible.findIndex((o, i) => i > active && starts(o));

      move(next >= 0 ? next : visible.findIndex(starts));
    }
  };

  return (
    // `modal`: a lista vira a camada de foco e de rolagem do momento — sem isso, dentro de
    // um diálogo a armadilha de foco dele devolveria o foco ao botão e a busca não
    // receberia o texto.
    <PopoverPrimitive.Root open={open} onOpenChange={openChange} modal>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          id={id}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          disabled={disabled}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              openChange(true);
            }
          }}
          data-slot="select-trigger"
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded-control border bg-white pr-2.5 pl-3 text-left text-ink',
            'hover:border-line-dashed disabled:opacity-60',
            open ? 'border-primary-focus ring-3 ring-primary-focus/15' : 'border-line-input',
            size === 'sm' ? 'h-9 rounded-control-sm text-small' : 'h-10 text-control max-md:text-small',
            className
          )}
        >
          {current?.leading}
          {current?.icon ? (
            <current.icon
              aria-hidden
              style={current.iconStyle}
              className={cn('size-4 shrink-0 stroke-2', current.iconClassName)}
            />
          ) : null}
          <span className={cn('min-w-0 flex-1 truncate', !current && 'text-ink-placeholder')}>
            {current?.label ?? placeholder}
          </span>
          <ChevronDownIcon
            aria-hidden
            className={cn(
              'size-4 shrink-0 stroke-2 text-ink-subtle transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          data-slot="select-content"
          aria-label={ariaLabel ?? placeholder}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (canSearch ? inputRef.current : listRef.current)?.focus();
          }}
          className={cn(
            'z-[90] flex flex-col rounded-block border border-line-input bg-white p-1 shadow-popover outline-none',
            // Com descrições, a lista fica mais larga e mais alta que a simples, para o texto respirar.
            described
              ? 'max-h-[min(440px,var(--radix-popover-content-available-height))] w-[max(var(--radix-popover-trigger-width),min(320px,calc(100vw-16px)))]'
              : 'max-h-[min(320px,var(--radix-popover-content-available-height))] w-[max(var(--radix-popover-trigger-width),220px)]',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0'
          )}
        >
          {canSearch ? (
            <div className="relative px-1 pt-1 pb-1.5">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 stroke-2 text-ink-placeholder"
              />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label="Pesquisar opções"
                aria-expanded
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeId}
                placeholder="Pesquisar..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onListKey}
                className="h-8.5 w-full rounded-control-sm border border-line-input bg-white pr-2.5 pl-8 text-small text-ink outline-none placeholder:text-ink-placeholder focus:border-primary-focus"
              />
            </div>
          ) : null}
          <div
            ref={attachList}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            aria-activedescendant={canSearch ? undefined : activeId}
            tabIndex={-1}
            onKeyDown={onListKey}
            className="relative flex min-h-0 flex-col gap-px overflow-y-auto outline-none"
          >
            {visible.map((o, i) => {
              const selected = o.value === value;

              return (
                <div key={o.value} className="contents">
                  {o.group && o.group !== visible[i - 1]?.group ? (
                    <div
                      role="presentation"
                      data-slot="select-group"
                      className={cn(
                        'px-2.5 pt-1.5 pb-1 text-caption font-bold tracking-[0.04em] text-ink-subtle uppercase',
                        i > pinnedCount && 'mt-1 border-t border-line-divider pt-2.5'
                      )}
                    >
                      {o.group}
                    </div>
                  ) : null}
                  <div
                    id={optionId(i)}
                    role="option"
                    aria-selected={selected}
                    // Com descrição, o nome é só o rótulo; o texto embaixo é a descrição.
                    aria-labelledby={o.description ? `${optionId(i)}-label` : undefined}
                    aria-describedby={o.description ? `${optionId(i)}-desc` : undefined}
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(o)}
                    onKeyDown={onListKey}
                    onMouseEnter={() => setActive(i)}
                    data-slot="select-item"
                    className={cn(
                      'flex min-h-8.5 cursor-pointer items-center gap-2 rounded-control-sm px-2.5 py-1.5 text-ink select-none',
                      size === 'sm' ? 'text-small' : 'text-control max-md:text-small',
                      i === active && 'bg-surface-muted',
                      selected && 'font-semibold'
                    )}
                  >
                    {o.leading}
                    {o.icon ? (
                      <o.icon
                        aria-hidden
                        style={o.iconStyle}
                        className={cn('size-4 shrink-0 stroke-2', o.iconClassName)}
                      />
                    ) : null}
                    {o.description ? (
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span id={`${optionId(i)}-label`} className="truncate">
                          {o.label}
                        </span>
                        <span
                          id={`${optionId(i)}-desc`}
                          className="text-caption font-normal text-pretty text-ink-subtle"
                        >
                          {o.description}
                        </span>
                      </span>
                    ) : (
                      <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    )}
                    {selected ? (
                      <CheckIcon aria-hidden className="size-4 shrink-0 stroke-[2.4] text-primary" />
                    ) : null}
                  </div>
                  {pinnedCount > 0 && i === pinnedCount - 1 && visible.length > pinnedCount ? (
                    <div
                      aria-hidden
                      data-slot="select-separator"
                      className="mx-1.5 my-1 h-px bg-line-divider"
                    />
                  ) : null}
                </div>
              );
            })}
            {!visible.length ? (
              <p className="m-0 p-2.5 text-small text-ink-subtle">Nenhuma opção encontrada.</p>
            ) : null}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
